import React, { PureComponent } from "react";
import styled from "styled-components";
import NumberFormat from "react-number-format";
import Fragment from "react-dot-fragment";
import { putRequest } from "../../../api/ApiService";
import { incrementUnacceptedDecisions } from "../../../api/SubmitDecisions";
import { logError } from "../../../services/utils";
import EventEmitter from "../../../services/event-emitter";
import { changeLocation } from "../../../api/Location";
import Auth from "../../../routes/Auth";
import Teams from "../../../api/Teams";
import ErrorAlert from "../../alerts/error";
import SuccessAlert from "../../alerts/success";
import FinanceStyled from "../../finance/style";

// Trailing slash matters: without it Django redirects 301 → /bulk/ and
// the browser-side PUT redirect drops the request body.
const PRICE_FORECASTS_BULK_URL = `${process.env.REACT_APP_API_URL}team/price_forecasts/bulk/`;

const FONT = "'Mukta Vaani', Lato, sans-serif";

// Validation bounds mirror what the existing pricing modal enforces
// (and what the backend serializer expects).
const PRICE_MIN = 0;
const PRICE_MAX = 99.99;
const FORECAST_MIN = 0;
const FORECAST_MAX = 99999999;

const fmtMoney = (n) => `$${(Math.round(Number(n) || 0)).toLocaleString("en-US")}`;
const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");

// ---------- styled components ----------

const Page = styled.div`
  padding: 24px 40px 64px;
  max-width: 1320px;
  margin: 0 auto;
  font-family: ${FONT};
`;

const ToastSlot = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  justify-content: center;
  pointer-events: none;
  margin-bottom: -12px;

  & > * {
    pointer-events: auto;
  }
`;

const HeaderBlock = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const TitleIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #DBEAFE;
  color: #2563EB;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: rgba(0, 27, 59, 0.6);
  line-height: 1.5;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  padding: 4px 10px;
  line-height: 1;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 28px;
`;

const KpiCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #E5E9F0;
  border-radius: 14px;
  padding: 20px 22px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 41, 0.04);
`;

const KpiIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  background: #EAF3EC;
  color: #2DA548;
  font-weight: 700;
`;

const KpiContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const KpiLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.2;
  margin-bottom: 6px;
`;

const KpiValue = styled.span`
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 27, 59, 0.94);
`;

const KpiAside = styled.div`
  margin-left: auto;
  font-size: 13px;
  color: rgba(0, 27, 59, 0.6);
  font-weight: 600;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
  color: rgba(0, 27, 59, 0.94);
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const BtnSecondary = styled.button`
  background: transparent;
  border: 1.5px solid #E5E9F0;
  color: rgba(0, 27, 59, 0.6);
  padding: 12px 22px;
  border-radius: 12px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: rgba(0, 27, 59, 0.6);
    color: rgba(0, 27, 59, 0.94);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BtnPrimary = styled.button`
  background: #3A507B;
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(58, 80, 123, 0.25);

  &:hover:not(:disabled) {
    background: #2E3F62;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  border: 1.5px solid ${p => (p.touched ? "#3A507B" : "#E5E9F0")};
  background: #FFFFFF;
  border-radius: 14px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${p => (p.touched
    ? "0 6px 20px rgba(58, 80, 123, 0.18)"
    : "none")};

  &:hover {
    box-shadow: ${p => (p.touched
      ? "0 8px 24px rgba(58, 80, 123, 0.24)"
      : "0 4px 16px rgba(15, 23, 41, 0.06)")};
  }
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProductIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #F5F7FB;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 27, 59, 0.6);
  flex-shrink: 0;
`;

const ProductName = styled.div`
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  color: rgba(0, 27, 59, 0.94);
  word-break: break-word;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

// Styled input wrappers so the NumberFormat-rendered <input> picks up
// the same focus/hover treatment as the surrounding cards. Styling the
// inner input by descendant selector — react-number-format v2 doesn't
// always forward className through styled().
const InputBox = styled.div`
  display: flex;
  align-items: center;
  background: #F5F7FB;
  border: 1.5px solid ${p => (p.invalid ? "#DC2626" : "#E5E9F0")};
  border-radius: 10px;
  padding: 10px 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${p => (p.invalid ? "#DC2626" : "#3A507B")};
    box-shadow: 0 0 0 3px ${p => (p.invalid
      ? "rgba(220, 38, 38, 0.12)"
      : "rgba(58, 80, 123, 0.12)")};
    background: #FFFFFF;
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: ${FONT};
    font-size: 16px;
    font-weight: 700;
    color: rgba(0, 27, 59, 0.94);
    text-align: right;
    font-variant-numeric: tabular-nums;
    width: 100%;
    min-width: 0;
    padding: 0;
  }
`;

const InputPrefix = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.35);
  margin-right: 8px;
`;

const InputSuffix = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 27, 59, 0.5);
  margin-left: 8px;
`;

const FieldError = styled.div`
  font-size: 12px;
  color: #DC2626;
  font-weight: 600;
  margin-top: 6px;
`;

const RevenueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 14px;
  border-top: 1px solid #E5E9F0;
`;

const RevenueLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const RevenueValue = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: rgba(0, 27, 59, 0.94);
  font-variant-numeric: tabular-nums;
`;

// ---------- $0-price confirmation modal ----------
//
// Fires when the user tries to Set Pricing while a released product
// still has a $0 price. Two buttons: Cancel returns them to the
// pricing table; Continue commits the save with the $0 price intact.

const ConfirmBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  z-index: 1200;
`;

const ConfirmCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 460px;
  padding: 28px 28px 24px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  font-family: 'Mukta Vaani', Lato, sans-serif;
  color: rgba(0, 27, 59, 0.94);
`;

const ConfirmTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 800;
`;

const ConfirmBody = styled.div`
  font-size: 16px;
  line-height: 1.55;
  margin-bottom: 20px;
`;

const ConfirmProductList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  font-weight: 700;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ConfirmSecondaryBtn = styled.button`
  padding: 10px 20px;
  border: 1.5px solid #D5D9DD;
  background: #ffffff;
  border-radius: 8px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;
`;

const ConfirmPrimaryBtn = styled.button`
  padding: 10px 20px;
  border: 0;
  background: #2563EB;
  border-radius: 8px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: #ffffff;
  cursor: pointer;
`;

// ---------- component ----------

class SetPricingPage extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      // Per-product local edit state, keyed by product id.
      // { [productId]: { price, forecast, forecast_id, player, invalidPrice, invalidForecast } }
      products: {},
      isSaving: false,
      errorMessage: null,
      successMessage: null,
      // Names of released products currently priced at $0. When
      // non-null, the "continue anyway?" confirmation modal renders
      // and the actual save is deferred until the user picks a button.
      zeroPriceProductNames: null,
    };
    this._successTimer = null;
    this.eventemitter = EventEmitter.getInstance();
  }

  goToCustomerAnalysis = e => {
    e.preventDefault();
    this.eventemitter.trigger("coaching-feedback-hide");
    changeLocation("/ceo/analysis/market");
  };

  showPopupHighPriceMobile = () => {
    this.eventemitter.trigger("coaching-feedback", [
      "Food for Thought",
      <Fragment>
        <b>Wow! You're looking to crush it this year!</b><br />
        But are you sure any mobile customer is willing to pay that high price for this product
      </Fragment>,
      "If so, how many units will the customer buy? I doubt any auto customers will buy",
      <Fragment>
        Before pricing your product, please double check <a href="/ceo/analysis/market" onClick={this.goToCustomerAnalysis}>customer analysis</a> to gain a better understanding of your customers needs/wants... you're welcome!
      </Fragment>,
    ]);
  };

  showPopupHighPriceAuto = () => {
    this.eventemitter.trigger("coaching-feedback", [
      "Food for Thought",
      <Fragment>
        <b>Wow! You're looking to crush it this year!</b><br />
        But are you sure any customer is willing to pay that high price for this product
      </Fragment>,
      "If so, how many units will the customer buy?",
      <Fragment>
        Before pricing your product, please double check <a href="/ceo/analysis/market" onClick={this.goToCustomerAnalysis}>customer analysis</a> to gain a better understanding of your customers needs/wants... you're welcome!
      </Fragment>,
    ]);
  };

  showPopupLowPrice = () => {
    this.eventemitter.trigger("coaching-feedback", [
      "Food for Thought",
      "I see what you're doing! Low prices, higher demand for your product. Econ 101, I get it...but you should consider a few things.",
      <Fragment>
        <b>Is the price too low?</b><br />
        At a certain low price point, customers might think your product must not be good and won't buy.
      </Fragment>,
      <Fragment>
        <b>How much of a Gross Margin (GM) will you make on each unit sold?</b><br />
        GM = (Selling Price - Unit Costs) / Selling Price. GM is also a key grading metric.
      </Fragment>,
    ]);
  };

  componentDidMount() {
    this._mounted = true;
    this.initProducts(this.props);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.products !== this.props.products
        || nextProps.price_forecasts !== this.props.price_forecasts) {
      this.initProducts(nextProps);
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._successTimer) {
      clearTimeout(this._successTimer);
      this._successTimer = null;
    }
  }

  // Seed each product row from the current price_forecasts entry. If
  // there's no live forecast for a product, default price and forecast
  // to 0 per the spec ("if no pricing exists, input zero value").
  initProducts = (props) => {
    const products = {};
    (props.products || []).forEach(product => {
      const pf = (props.price_forecasts || [])
        .find(i => Number(i.product) === Number(product.id));
      products[product.id] = {
        name: product.name,
        price: pf ? Number(pf.price) || 0 : 0,
        forecast: pf ? Number(pf.forecast) || 0 : 0,
        forecast_id: pf ? pf.id : 0,
        player: pf ? pf.player : null,
        invalidPrice: false,
        invalidForecast: false,
      };
    });
    this.setState({ products });
  };

  // Single update entry-point. Keeps validation flags in sync so the
  // red ring clears as soon as the user fixes the value.
  setField = (productId, key, value) => {
    const products = { ...this.state.products };
    const row = products[productId];
    if (!row) return;
    const next = { ...row, [key]: value };
    if (key === "price") {
      next.invalidPrice = !(value >= PRICE_MIN && value <= PRICE_MAX);
    } else if (key === "forecast") {
      next.invalidForecast = !(value >= FORECAST_MIN && value <= FORECAST_MAX);
    }
    products[productId] = next;
    this.setState({ products });
  };

  onPriceChange = (productId) => (e, values) => {
    const raw = (values && typeof values.floatValue === "number")
      ? values.floatValue
      : 0;
    this.setField(productId, "price", raw);
  };

  onForecastChange = (productId) => (e, values) => {
    const raw = (values && typeof values.floatValue === "number")
      ? values.floatValue
      : 0;
    this.setField(productId, "forecast", raw);
  };

  resetAll = () => {
    // Reset to the values currently saved on the backend, not zeros —
    // matches the convention in the rest of the app where "Reset" means
    // "discard my unsaved edits."
    this.initProducts(this.props);
  };

  // ---------- totals ----------

  productList = () => {
    const { products = [] } = this.props;
    return (products || []).map(p => ({
      id: p.id,
      ...this.state.products[p.id],
    })).filter(p => p.name != null);
  };

  get totalForecastRevenue() {
    return this.productList().reduce(
      (sum, row) => sum + (Number(row.price) || 0) * (Number(row.forecast) || 0),
      0
    );
  }

  // ---------- save ----------

  hideErrorMessage = () => {
    this.setState({ errorMessage: null });
  };

  showLocalSuccess = (message) => {
    if (this._successTimer) clearTimeout(this._successTimer);
    this.setState({ successMessage: message });
    this._successTimer = setTimeout(() => {
      if (!this._mounted) return;
      this.setState({ successMessage: null });
    }, 5000);
  };

  // The bulk endpoint is scoped to (team, round). team and player come
  // from auth/Teams; round id is looked up from the rounds array.
  resolveBulkContext = () => {
    const { rounds = [], currentRound } = this.props;
    const teamId = Auth.getTeamId();
    const player = Teams.currentPlayer();
    const playerId = player ? player.id : null;
    const round = rounds.find(r => Number(r.number) === Number(currentRound));
    const roundId = round ? round.id : null;
    return { teamId, playerId, roundId };
  };

  // Whether any product on the team currently lacks a saved forecast row.
  // Used to decide whether to bump the unaccepted-decisions counter
  // after a successful bulk save (only counts the first time a forecast
  // appears for a product).
  get hasNewForecast() {
    const rows = this.productList();
    return rows.some(row => !row.forecast_id || row.forecast_id <= 0);
  }

  postPricing = () => {
    if (!this.props.actionsAllowed) return;

    const rows = this.productList();
    if (rows.length === 0) {
      this.setState({
        errorMessage: "There are no products to price. Please coordinate with Product Development first.",
      });
      return;
    }

    // Validate every row before sending anything; surface a single
    // error message if any value is out of range.
    const products = { ...this.state.products };
    let hasInvalid = false;
    rows.forEach(row => {
      const invalidPrice = !(row.price >= PRICE_MIN && row.price <= PRICE_MAX);
      const invalidForecast = !(row.forecast >= FORECAST_MIN && row.forecast <= FORECAST_MAX);
      if (invalidPrice || invalidForecast) hasInvalid = true;
      products[row.id] = { ...products[row.id], invalidPrice, invalidForecast };
    });
    if (hasInvalid) {
      this.setState({
        products,
        errorMessage: `Price must be between $${PRICE_MIN} and $${PRICE_MAX}, and unit forecast must be between ${fmtInt(FORECAST_MIN)} and ${fmtInt(FORECAST_MAX)}.`,
      });
      return;
    }

    // Soft-warn on released products (months_available_to_produce > 0)
    // still priced at $0. PRICE_MIN is 0, so the range check above
    // lets these through, but shipping a released product for $0 is
    // almost always a mistake. Missing months_available_to_produce is
    // treated as "released" per the game rule so we don't quietly
    // let a $0 price slip through when the field is absent.
    const productsById = new Map(
      (this.props.products || []).map(p => [Number(p.id), p])
    );
    const zeroPriceReleasedNames = rows
      .filter(row => Number(row.price) === 0)
      .filter(row => {
        const p = productsById.get(Number(row.id));
        const months = p && p.months_available_to_produce != null
          ? Number(p.months_available_to_produce)
          : 1;
        return months > 0;
      })
      .map(row => row.name);
    if (zeroPriceReleasedNames.length > 0) {
      this.setState({ zeroPriceProductNames: zeroPriceReleasedNames });
      return;
    }

    this.doPostPricing(rows);
  };

  confirmZeroPricePost = () => {
    this.setState({ zeroPriceProductNames: null });
    this.doPostPricing(this.productList());
  };

  cancelZeroPricePost = () => {
    this.setState({ zeroPriceProductNames: null });
  };

  doPostPricing = (rows) => {
    const { teamId, playerId, roundId } = this.resolveBulkContext();
    if (!teamId || !playerId || !roundId) {
      this.setState({
        errorMessage: "We couldn't determine your team, round, or player context. Please refresh the page and try again.",
      });
      return;
    }

    const willCreate = this.hasNewForecast;
    // True when NO product's forecast has been posted by a player yet.
    // Used with currentRound === 1 to detect the student's very
    // first pricing decision so PMDecisions can show the coaching
    // popup that reminds them about the other two marketing
    // decisions (promotion + sales channel).
    //
    // Test `player`, not `forecast_id` — the backend pre-seeds a
    // price_forecasts row for every product with a valid id but
    // player=null. A decision is "made" only once a player is
    // attached to the record (mirrors the Set Allocation trigger
    // in manage-promotion-page.js).
    const wasFirstPricingRd1 =
      Number(this.props.currentRound) === 1
      && rows.every(row => row.player == null);
    const forecasts = rows.map(row => ({
      product: row.id,
      price: Number(row.price) || 0,
      forecast: Number(row.forecast) || 0,
    }));

    const body = {
      team: teamId,
      round: roundId,
      player: playerId,
      forecasts,
    };

    this.setState({ isSaving: true });

    putRequest(PRICE_FORECASTS_BULK_URL).send(body)
      .then(res => {
        // Coaching popups (ported from ModalPMDecisionsRevenueForecast).
        // The bulk endpoint may return either an array or a shape like
        // { forecasts: [...] } / { results: [...] }; grab whichever is
        // present so we can scan per-forecast feedback flags.
        const bodyRes = (res && res.body) || {};
        const forecastRows = Array.isArray(bodyRes)
          ? bodyRes
          : (bodyRes.forecasts || bodyRes.results || []);
        const anyMobileHigh = forecastRows.some(r => Number(r.high_price_mobile_feedback) === 1);
        const anyAutoHigh = forecastRows.some(r => Number(r.high_price_auto_feedback) === 1);
        const anyLowPrice = forecastRows.some(r => Number(r.price) > 0 && Number(r.price) <= 5);
        const anyAutoLow = forecastRows.some(r => Number(r.low_price_auto_feedback) === 1);
        if (anyMobileHigh) {
          this.showPopupHighPriceMobile();
        } else if (anyAutoHigh) {
          this.showPopupHighPriceAuto();
        } else if (anyLowPrice || anyAutoLow) {
          this.showPopupLowPrice();
        }

        if (willCreate) {
          incrementUnacceptedDecisions("marketing");
        }
        if (this.props.reloadData) this.props.reloadData();
        if (this._mounted) {
          this.setState({ isSaving: false });
          this.showLocalSuccess("Pricing and unit forecast were successfully saved.");
        }
        if (wasFirstPricingRd1 && this.props.onFirstPricingSet) {
          this.props.onFirstPricingSet();
        }
      })
      .catch(err => {
        logError(err, "SetPricingPage", "postPricing");
        if (this._mounted) {
          this.setState({
            isSaving: false,
            errorMessage: "Something went wrong saving your pricing. Please try again.",
          });
        }
      });
  };

  // ---------- render ----------

  // Product card icon — simple price-tag glyph. Doesn't depend on the
  // product name so every product gets the same affordance.
  renderProductIcon() {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        width="22" height="22">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    );
  }

  renderProductCard = (product) => {
    const { actionsAllowed } = this.props;
    const row = this.state.products[product.id];
    if (!row) return null;
    const expectedRevenue = (Number(row.price) || 0) * (Number(row.forecast) || 0);
    const touched = (Number(row.price) || 0) > 0 || (Number(row.forecast) || 0) > 0;

    return (
      <ProductCard key={product.id} touched={touched}>
        <ProductHeader>
          <ProductIconWrap>{this.renderProductIcon()}</ProductIconWrap>
          <ProductName>Product {row.name}</ProductName>
        </ProductHeader>

        <div>
          <FieldLabel>Set Price</FieldLabel>
          <InputBox invalid={row.invalidPrice}>
            <InputPrefix>$</InputPrefix>
            <NumberFormat
              value={row.price}
              thousandSeparator=","
              decimalSeparator="."
              decimalPrecision={2}
              allowNegative={false}
              onChange={this.onPriceChange(product.id)}
              disabled={!actionsAllowed}
            />
          </InputBox>
          {row.invalidPrice && (
            <FieldError>Price must be between ${PRICE_MIN} and ${PRICE_MAX}.</FieldError>
          )}
        </div>

        <div>
          <FieldLabel>Set Unit Forecast</FieldLabel>
          <InputBox invalid={row.invalidForecast}>
            <NumberFormat
              value={row.forecast}
              thousandSeparator=","
              allowNegative={false}
              onChange={this.onForecastChange(product.id)}
              disabled={!actionsAllowed}
            />
            <InputSuffix>units</InputSuffix>
          </InputBox>
          {row.invalidForecast && (
            <FieldError>Forecast must be between {fmtInt(FORECAST_MIN)} and {fmtInt(FORECAST_MAX)}.</FieldError>
          )}
        </div>

        <RevenueRow>
          <RevenueLabel>Expected Revenue</RevenueLabel>
          <RevenueValue>{fmtMoney(expectedRevenue)}</RevenueValue>
        </RevenueRow>
      </ProductCard>
    );
  };

  render() {
    const { products = [], actionsAllowed, onClose } = this.props;
    const { isSaving, errorMessage, successMessage, zeroPriceProductNames } = this.state;
    const totalRevenue = this.totalForecastRevenue;

    return (
      <FinanceStyled.LenderOverlay>
        {errorMessage && (
          <ErrorAlert
            title="Heads up"
            message={errorMessage}
            onHide={this.hideErrorMessage}
          />
        )}
        {zeroPriceProductNames && zeroPriceProductNames.length > 0 && (
          <ConfirmBackdrop role="dialog" aria-modal="true">
            <ConfirmCard>
              <ConfirmTitle>Continue with a $0 price?</ConfirmTitle>
              <ConfirmBody>
                {zeroPriceProductNames.length === 1
                  ? "This released product still has a $0 selling price:"
                  : "These released products still have a $0 selling price:"}
                <ConfirmProductList>
                  {zeroPriceProductNames.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ConfirmProductList>
                <div style={{ marginTop: 12 }}>
                  Selling at $0 means no revenue on those units. Go back to
                  set a price, or continue anyway.
                </div>
              </ConfirmBody>
              <ConfirmActions>
                <ConfirmSecondaryBtn onClick={this.cancelZeroPricePost}>
                  Go Back
                </ConfirmSecondaryBtn>
                <ConfirmPrimaryBtn onClick={this.confirmZeroPricePost}>
                  Continue
                </ConfirmPrimaryBtn>
              </ConfirmActions>
            </ConfirmCard>
          </ConfirmBackdrop>
        )}
        <Page>
          {successMessage && (
            <ToastSlot>
              <SuccessAlert message={successMessage} />
            </ToastSlot>
          )}
          <HeaderBlock>
            <HeaderLeft>
              <TitleRow>
                <TitleIcon>💲</TitleIcon>
                <Title>Set Pricing</Title>
              </TitleRow>
              <Subtitle>
                Set the selling price and unit forecast for each of your products.
              </Subtitle>
            </HeaderLeft>
            <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
          </HeaderBlock>

          <KpiRow>
            <KpiCard>
              <KpiIcon>$</KpiIcon>
              <KpiContent>
                <KpiLabel>Total Forecast Revenue</KpiLabel>
                <KpiValue>{fmtMoney(totalRevenue)}</KpiValue>
              </KpiContent>
              <KpiAside>
                {products.length} {products.length === 1 ? "product" : "products"}
              </KpiAside>
            </KpiCard>
          </KpiRow>

          <SectionHeader>
            <SectionTitle>Product Pricing</SectionTitle>
            <FooterButtons>
              <BtnSecondary onClick={this.resetAll} disabled={!actionsAllowed || isSaving}>
                Reset All
              </BtnSecondary>
              <BtnPrimary
                onClick={this.postPricing}
                disabled={!actionsAllowed || isSaving}
              >
                <span>{isSaving ? "Saving…" : "Set Pricing"}</span>
                {!isSaving && <span>→</span>}
              </BtnPrimary>
            </FooterButtons>
          </SectionHeader>

          {products.length === 0 ? (
            <ProductCard>
              <ProductName>No products available yet.</ProductName>
              <Subtitle>
                Coordinate with your Head of Product (or CEO) to develop a product first.
              </Subtitle>
            </ProductCard>
          ) : (
            <ProductsGrid>
              {products.map(this.renderProductCard)}
            </ProductsGrid>
          )}
        </Page>
      </FinanceStyled.LenderOverlay>
    );
  }
}

export default SetPricingPage;



// WEBPACK FOOTER //
// ./src/components/pm/decisions/set-pricing-page.js